import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { loginApi, logoutApi } from '../api/auth';
import { getPendingWrites, performSharedTokenRefresh } from '../api/client';
import { ApiEnvironment, API_URLS, readApiEnvironment, readCustomApiUrl, saveApiEnvironment } from '../config/env';
import { SavedSession, LOGIN_EMAIL_STORAGE_KEY, activateSession, clearSession, readSession, readSavedSession,
  activeSessionId, listSessions, saveSession, subscribeSession, migrateLegacySession, normalizeApiUrl, sessionVersion } from './session';

export interface LoginDestination { environment: ApiEnvironment; customUrl?: string; sessionId?: string; }
interface AuthContextValue {
  isAuthenticated: boolean; isRestoring: boolean; activeSession: SavedSession | null;
  sessions: SavedSession[]; activation: number;
  login(email: string, password: string, destination?: LoginDestination): Promise<void>;
  switchSession(id: string): Promise<void>;
  logout(id?: string): Promise<void>;
  authNotice: string | null; clearAuthNotice(): void;
}
const AuthContext = createContext<AuthContextValue | null>(null);
const notices: Record<string, string> = {
  'session-expired': 'Your session expired. Sign in again.',
  'admin-required': 'Administrator access required.',
};
function assertIdle() {
  if (getPendingWrites()) throw new Error('Wait for the current action to finish before changing sessions.');
}
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeSession, setActive] = useState<SavedSession | null>(null);
  const [sessions, setSessions] = useState<SavedSession[]>([]);
  const [activation, setActivation] = useState(0);
  const [isRestoring, setRestoring] = useState(true);
  const [authNotice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    const sync = (session: SavedSession | null, reason?: string) => {
      if (!mounted) return;
      const id = activeSessionId();
      setActive(session || (id ? readSavedSession(id) : null));
      setSessions(listSessions());
      setActivation(sessionVersion());
      if (reason && notices[reason]) setNotice(notices[reason]);
    };
    const unsubscribe = subscribeSession(sync);
    migrateLegacySession();
    const initial = readSession();
    sync(initial);
    (async () => {
      try {
        if (initial && initial.expiresAt <= Date.now()) await performSharedTokenRefresh(initial.id);
      } catch (error) {
        if (mounted && readSession()?.id === initial?.id) {
          activateSession(null);
          setNotice(error instanceof Error ? error.message : 'Could not restore session. Try again.');
        }
      } finally { if (mounted) setRestoring(false); }
    })();
    return () => { mounted = false; unsubscribe(); };
  }, []);
  const login = useCallback(async (email: string, password: string, destination?: LoginDestination) => {
    assertIdle();
    const environment = destination?.environment || readApiEnvironment();
    const apiUrl = normalizeApiUrl(environment === 'other' ? destination?.customUrl || readCustomApiUrl() : API_URLS[environment]);
    const normalizedEmail = email.trim().toLowerCase();
    const pair = await loginApi(normalizedEmail, password, apiUrl);
    assertIdle();
    const existing = listSessions().find(session => session.apiUrl === apiUrl && session.email.toLowerCase() === normalizedEmail);
    const reauthenticating = destination?.sessionId ? readSavedSession(destination.sessionId) : null;
    const legacyId = reauthenticating?.email === 'Existing session' && reauthenticating.apiUrl === apiUrl
      ? reauthenticating.id : undefined;
    const session: SavedSession = {
      id: existing?.id || legacyId || encodeURIComponent(apiUrl + '\n' + normalizedEmail), email: normalizedEmail, environment, apiUrl,
      accessToken: pair.access_token, refreshToken: pair.refresh_token, expiresAt: Date.now() + pair.expires_in * 1000,
    };
    localStorage.setItem(LOGIN_EMAIL_STORAGE_KEY, normalizedEmail);
    saveApiEnvironment(environment, environment === 'other' ? apiUrl : undefined);
    saveSession(session);
    activateSession(session.id);
    setNotice(null);
  }, []);
  const switchSession = useCallback(async (id: string) => {
    assertIdle();
    const session = readSavedSession(id);
    if (!session || session.needsLogin) throw new Error('Sign in again to use this session.');
    if (session.expiresAt <= Date.now()) await performSharedTokenRefresh(id);
    assertIdle();
    if (!readSavedSession(id) || readSavedSession(id)?.needsLogin) throw new Error('Sign in again to use this session.');
    activateSession(id);
    setNotice(null);
  }, []);
  const logout = useCallback(async (id = readSession()?.id) => {
    assertIdle();
    const session = id ? readSavedSession(id) : null;
    if (!session) return;
    try {
      if (session.refreshToken) await logoutApi(session.refreshToken, session.apiUrl);
    } catch { /* Local removal still succeeds when the server is unavailable. */ }
    finally { clearSession('logout', undefined, session.id); }
  }, []);
  return <AuthContext.Provider value={{ isAuthenticated: !!activeSession, isRestoring, activeSession, sessions, activation,
    login, switchSession, logout, authNotice, clearAuthNotice: () => setNotice(null) }}>{children}</AuthContext.Provider>;
};
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within an AuthProvider');
  return value;
}
